import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

/**
 * Reusable Internal Chat empty state (SPEC-2026-07-internal-chat, T-25).
 *
 * Icon + title + hint + one optional call to action. Every text input is an
 * **i18n key**, translated in this component's template (never in a lifecycle
 * hook), so hosts stay declarative:
 *
 * ```html
 * <!-- no conversations at all -->
 * <chat-empty-state
 *   icon="ri-chat-new-line"
 *   titleKey="chat.states.emptyConversations"
 *   hintKey="chat.states.emptyConversationsHint"
 *   actionKey="chat.states.startConversation"
 *   (action)="openNewConversationDialog()"
 * ></chat-empty-state>
 *
 * <!-- conversation open with no messages yet -->
 * <chat-empty-state titleKey="chat.states.emptyMessages" hintKey="chat.states.emptyMessagesHint"></chat-empty-state>
 * ```
 */
@Component({
  selector: 'chat-empty-state',
  templateUrl: './chat-empty-state.component.html',
  styleUrls: ['./chat-state-panel.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatEmptyStateComponent {

  @Input() icon = 'ri-chat-3-line';

  /** i18n key of the headline. */
  @Input() titleKey = 'chat.states.emptyConversations';

  /** i18n key of the supporting line — empty/null renders no hint. */
  @Input() hintKey: string | null = 'chat.states.emptyConversationsHint';

  /** i18n key of the call to action — null renders no button. */
  @Input() actionKey: string | null = null;

  @Input() actionIcon = 'ri-chat-new-line';

  /** Tighter spacing/typography for the bubble popup surface. */
  @Input() compact = false;

  @Output() action = new EventEmitter<void>();
}
